import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll, AnimatePresence } from "framer-motion";
import { ArrowDownRight, Truck } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { scrollToId } from "./Smooth";
import { Wave } from "./Wave";
import { ProductBadges } from "./ProductBadges";

const ease = [0.22, 1, 0.36, 1];
const IMGS = ["/img/front.png", "/img/left.png", "/img/right.png"];

const MaskLine = ({ children, i, italic }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${italic ? "italic text-[#0A7189]" : ""}`}
      initial={{ y: "105%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const Bottle = ({ angle }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative mx-auto aspect-square w-full max-w-[520px]" style={{ perspective: 1200 }} data-testid="hero-bottle">
      <div className="absolute inset-[8%] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.75)_0%,rgba(126,217,210,0.35)_40%,transparent_70%)] blur-2xl" />
      <div className="absolute inset-[14%] rounded-full border border-white/60" />
      <div className="absolute inset-[4%] rounded-full border border-white/40" />
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative h-full w-full">
        <div className="floaty h-full w-full">
          <AnimatePresence initial={false}>
            <motion.img
              key={angle}
              src={IMGS[angle]}
              alt="Aussie Trace Minerals Complete Liquid Electrolytes 240ml"
              className="absolute inset-0 h-full w-full object-contain drop-shadow-[0_40px_60px_rgba(7,59,76,0.45)]"
              initial={{ opacity: 0, scale: 0.94, rotate: -3 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 1.04 }}
              transition={{ duration: 0.7, ease }}
            />
          </AnimatePresence>
        </div>
      </motion.div>
      <div className="absolute bottom-[6%] left-1/2 h-6 w-1/2 -translate-x-1/2 rounded-[50%] bg-[#073B4C]/35 blur-xl" />
    </div>
  );
};

export const Hero = () => {
  const { t } = useLang();
  const [angle, setAngle] = useState(0);
  const sec = useRef(null);
  const { scrollYProgress } = useScroll({ target: sec, offset: ["start start", "end start"] });
  const yBottle = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const big = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section ref={sec} className="relative overflow-hidden bg-[#FBF7EF] pt-24 text-[#073B4C] lg:min-h-[100svh]" data-testid="hero-section">
      <motion.img src="/img/shore.jpg" alt="" className="absolute inset-0 h-full w-full object-cover object-[72%_50%] lg:object-center"
        initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 2.6, ease }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#FBF7EF]/80 via-[#FBF7EF]/45 to-[#FBF7EF]/10 lg:bg-gradient-to-r lg:from-[#FBF7EF]/55 lg:via-transparent lg:to-transparent" />
      <motion.p style={{ x: big }} className="pointer-events-none absolute bottom-6 left-0 whitespace-nowrap font-display text-[22vw] leading-none text-[#073B4C]/[0.06]" aria-hidden="true">
        Mareia · Ionique · Mareia
      </motion.p>

      <div className="relative mx-auto grid max-w-7xl items-center gap-8 px-5 pb-28 md:px-10 lg:grid-cols-12 lg:pb-32">
        <motion.div style={{ y: yText }} className="lg:col-span-6">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="font-mono-x text-xs uppercase tracking-[0.22em] text-[#0A7189]" data-testid="hero-kicker">{t.hero.kicker}</motion.p>
          <h1 className="mt-6 font-display text-[3.4rem] leading-[0.92] sm:text-7xl lg:text-[5.4rem] xl:text-[6.3rem]" data-testid="hero-title">
            {t.hero.lines.map((l, i) => <MaskLine key={`${l}-${i}`} i={i} italic={i === 1}>{l}</MaskLine>)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.9, ease }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[#073B4C]/85 md:text-lg">{t.hero.sub}</motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease }}
            className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
            <button onClick={() => scrollToId("order")} data-testid="hero-register-button"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#073B4C] px-7 py-4 font-semibold text-[#FBF7EF] transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#0A7189]">
              {t.hero.cta}
              <ArrowDownRight className="h-5 w-5 transition-transform group-hover:rotate-[-45deg]" />
            </button>
            <button onClick={() => scrollToId("benefits")} data-testid="hero-explore-button"
              className="rounded-full border border-[#073B4C]/30 bg-white/30 px-7 py-4 text-[#073B4C] backdrop-blur transition-colors hover:border-[#073B4C] hover:bg-white/60">
              {t.hero.cta2}
            </button>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white/55 px-3.5 py-2 text-sm text-[#073B4C] backdrop-blur" data-testid="hero-shipping-note">
            <Truck className="h-4 w-4 shrink-0 text-[#0A7189]" /> {t.hero.ship}
          </motion.p>
        </motion.div>

        <motion.div style={{ y: yBottle }} className="relative lg:col-span-6"
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.5, ease }}>
          <div className="grid items-center gap-4 sm:grid-cols-[1fr_200px]">
            <Bottle angle={angle} />
            <ProductBadges />
          </div>
          <div className="mt-2 flex items-end justify-between gap-4">
            <div className="flex gap-2" data-testid="hero-angle-switcher">
              {t.hero.angles.map((a, i) => (
                <button key={a} onClick={() => setAngle(i)} data-testid={`hero-angle-${i}`}
                  className={`rounded-full px-3 py-1.5 font-mono-x text-xs uppercase tracking-wider transition-colors ${angle === i ? "bg-[#073B4C] text-[#FBF7EF]" : "border border-white/70 bg-white/40 text-[#073B4C] backdrop-blur hover:bg-white/70"}`}>
                  {a}
                </button>
              ))}
            </div>
            <div className="rounded-2xl bg-white/55 px-4 py-2 text-right backdrop-blur" data-testid="hero-price">
              <p className="font-display whitespace-nowrap text-2xl text-[#073B4C] sm:text-3xl">{t.hero.price}</p>
              <p className="text-xs text-[#073B4C]/80">{t.hero.priceNote}</p>
            </div>
          </div>
        </motion.div>
      </div>
      <Wave fill="#FBF7EF" />
    </section>
  );
};
